import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm, useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  Plus,
  Trash2,
  Loader2,
  Search,
  ShoppingCart,
  Package,
  User,
  Calendar,
  FileText,
  Calculator,
} from 'lucide-react';
import { format, addDays } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Combobox } from '@/components/ui/combobox';
import { CounterpartyPicker } from '@/components/trading/sales/counterparty-picker';
import { trpc } from '@/lib/trpc';
import { calculateSalesTotals } from '@/lib/sales-totals';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';
import { CreateCustomerModal } from '@/components/trading/create-customer-modal';

interface QuoteItemForm {
  productId?: string;
  productName?: string;
  productCode?: string;
  description?: string; // required for a service line (no productId)
  quantity: number;
  unitPrice: number;
  discountPercent: number;
  taxRate: number;
  notes?: string;
}

interface SalesQuoteForm {
  customerId?: string;
  supplierId?: string;
  companyName?: string;
  contactName: string;
  email: string;
  phone?: string;
  validDays: number;
  discountPercent: number;
  currency: string;
  paymentTerms?: string;
  deliveryTerms?: string;
  notes?: string;
  items: QuoteItemForm[];
}

export function NewSalesQuotePage() {
  const navigate = useNavigate();
  const { t } = useTranslation('trading');
  const [productSearch, setProductSearch] = useState('');

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
  } = useForm<SalesQuoteForm>({
    defaultValues: {
      validDays: 30,
      discountPercent: 0,
      currency: 'EUR',
      items: [],
    },
  });

  const { fields: items, append: appendItem, remove: removeItem } = useFieldArray({
    control,
    name: 'items',
  });

  // Queries
  const { data: customersData } = trpc.tradingCustomer.list.useQuery({ pageSize: 100 });
  const { data: productsData } = trpc.tradingProduct.list.useQuery(
    { search: productSearch },
    { enabled: productSearch.length >= 2 }
  );

  const customers = customersData?.customers ?? [];
  const products = productsData?.products ?? [];

  const watchedValues = watch();
  const selectedCustomerId = watchedValues.customerId;

  // Imperative price-resolver — used when adding a line.
  const utils = trpc.useUtils();

  // Create mutation
  const createMutation = trpc.tradingSalesQuote.create.useMutation({
    onSuccess: (data) => {
      showSuccessToast(t('common.created'));
      navigate(`/trading/sales/quotes/${data.id}`);
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const { subtotal, discountAmount, taxAmount, total } = calculateSalesTotals(watchedValues.items, watchedValues.discountPercent);

  const handleAddProduct = async (productId: string) => {
    const product = products.find((p) => p.id === productId);
    if (!product) return;

    // Check if product already exists in items
    const existingIndex = items.findIndex((item) => item.productId === productId);
    if (existingIndex >= 0) {
      // Increment quantity instead of adding new line
      const currentQty = watchedValues.items[existingIndex]?.quantity || 0;
      setValue(`items.${existingIndex}.quantity`, currentQty + 1);
      return;
    }

    // Auto-fill price + KDV from the right side of the product based on
    // counterparty kind. Empty inputs when that side hasn't been configured.
    const kind: 'customer' | 'vendor' = watchedValues.supplierId ? 'vendor' : 'customer';
    let unitPrice = 0;
    let taxRate = 0;
    try {
      const resolved = await utils.tradingProduct.resolvePrice.fetch({
        productId: product.id,
        kind,
      });
      unitPrice = resolved.unitPrice ?? 0;
      taxRate = resolved.kdvRate ?? 0;
      if (resolved.unitPrice == null) showErrorToast(t('products.priceNotConfigured'));
    } catch {
      showErrorToast(t('products.priceLoadError'));
    }

    appendItem({
      productId: product.id,
      productName: product.name,
      productCode: product.fullCode,
      quantity: 1,
      unitPrice,
      discountPercent: 0,
      taxRate,
      notes: '',
    });

    setProductSearch('');
  };

  const handleAddCustomLine = () => {
    appendItem({
      description: '',
      quantity: 1,
      unitPrice: 0,
      discountPercent: 0,
      taxRate: 0,
      notes: '',
    });
  };

  const handleCustomerSelect = (customerId: string) => {
    setValue('customerId', customerId);
    setValue('supplierId', undefined);
    const customer = customers.find((c) => c.id === customerId);
    if (customer) {
      setValue('contactName', customer.contactName || '');
      setValue('email', customer.email || '');
      setValue('phone', customer.phone || '');
      setValue('companyName', customer.companyName || '');
    }
  };

  const handleCounterpartySelect = (sel: import('@/components/trading/sales/counterparty-picker').CounterpartySelection) => {
    if (sel.kind === 'customer') {
      handleCustomerSelect(sel.id);
      return;
    }
    setValue('supplierId', sel.id);
    setValue('customerId', undefined);
    setValue('contactName', sel.contactName || '');
    setValue('email', sel.email || '');
    setValue('phone', sel.phone || '');
    setValue('companyName', sel.companyName || '');
  };

  const handleCounterpartyClear = () => {
    setValue('customerId', undefined);
    setValue('supplierId', undefined);
  };

  const handleCustomerCreated = (customer: { id: string; companyName: string; contactName: string; email: string; phone: string | null }) => {
    setValue('customerId', customer.id);
    setValue('companyName', customer.companyName || '');
    setValue('contactName', customer.contactName || '');
    setValue('email', customer.email || '');
    setValue('phone', customer.phone || '');
  };

  const onSubmit = (data: SalesQuoteForm) => {
    if (!data.contactName || !data.email) {
      showErrorToast(t('sales.validation.enterContactAndEmail'));
      return;
    }

    if (data.items.length === 0) {
      showErrorToast(t('sales.validation.addProduct'));
      return;
    }

    const missingDescription = data.items.some((item) => !item.productId && !item.description?.trim());
    if (missingDescription) {
      showErrorToast(t('sales.validation.enterDescription'));
      return;
    }

    createMutation.mutate({
      tradingCustomerId: data.customerId || undefined,
      tradingSupplierId: data.supplierId || undefined,
      companyName: data.companyName || undefined,
      contactName: data.contactName,
      email: data.email,
      phone: data.phone || undefined,
      validDays: data.validDays,
      discountPercent: data.discountPercent,
      currency: data.currency,
      paymentTerms: data.paymentTerms || undefined,
      deliveryTerms: data.deliveryTerms || undefined,
      notes: data.notes || undefined,
      items: data.items.map((item) => ({
        productId: item.productId || undefined,
        description: item.productId ? undefined : item.description,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        discountPercent: item.discountPercent,
        taxRate: item.taxRate,
        notes: item.notes || undefined,
      })),
    });
  };

  const formatCurrency = (value: number, currency: string = 'EUR') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(value);
  };

  const selectedCustomer = customers.find((c) => c.id === selectedCustomerId);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/trading/sales/quotes"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            {t('common.back')}
          </Link>
          <h1 className="text-2xl font-bold">{t('sales.quotes.addQuote')}</h1>
        </div>
        <Button
          onClick={handleSubmit(onSubmit)}
          disabled={createMutation.isPending}
        >
          {createMutation.isPending && (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          )}
          {t('common.save')}
        </Button>
      </div>

      <form className="grid gap-6 lg:grid-cols-3">
        {/* Main Content */}
        <div className="space-y-6 lg:col-span-2">
          {/* Customer Section */}
          <div className="border border-border bg-card p-6">
            <h2 className="flex items-center gap-2 font-semibold mb-4">
              <User className="h-5 w-5" />
              {t('sales.quotes.customer')}
            </h2>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>{t('sales.quotes.selectCustomer')}</Label>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <CounterpartyPicker
                      customerId={watchedValues.customerId}
                      supplierId={watchedValues.supplierId}
                      onSelect={handleCounterpartySelect}
                      onClear={handleCounterpartyClear}
                    />
                  </div>
                  <CreateCustomerModal onCustomerCreated={handleCustomerCreated} />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="companyName">{t('sales.quotes.company')}</Label>
                <Input
                  id="companyName"
                  {...register('companyName')}
                  placeholder={t('sales.quotes.companyPlaceholder')}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contactName">{t('sales.quotes.contactName')} *</Label>
                <Input
                  id="contactName"
                  {...register('contactName', { required: true })}
                  placeholder={t('sales.quotes.contactNamePlaceholder')}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">{t('sales.quotes.email')} *</Label>
                <Input
                  id="email"
                  type="email"
                  {...register('email', { required: true })}
                  placeholder={t('sales.quotes.emailPlaceholder')}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">{t('sales.quotes.phone')}</Label>
                <Input
                  id="phone"
                  {...register('phone')}
                  placeholder={t('sales.quotes.phonePlaceholder')}
                />
              </div>
            </div>
          </div>

          {/* Quote Details */}
          <div className="border border-border bg-card p-6">
            <h2 className="flex items-center gap-2 font-semibold mb-4">
              <Calendar className="h-5 w-5" />
              {t('sales.quotes.quoteDetails')}
            </h2>

            <div className="grid gap-4 sm:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="validDays">{t('sales.quotes.validDays')}</Label>
                <Input
                  type="number"
                  id="validDays"
                  {...register('validDays', { valueAsNumber: true })}
                  min="1"
                />
                <p className="text-xs text-muted-foreground">
                  {t('sales.quotes.validUntil')}: {format(addDays(new Date(), watchedValues.validDays || 30), 'MMM d, yyyy')}
                </p>
              </div>

              <div className="space-y-2">
                <Label>{t('sales.quotes.currency')}</Label>
                <Combobox
                  options={[
                    { value: 'EUR', label: 'EUR' },
                    { value: 'USD', label: 'USD' },
                    { value: 'GBP', label: 'GBP' },
                    { value: 'TRY', label: 'TRY' },
                  ]}
                  value={watchedValues.currency}
                  onValueChange={(value) => setValue('currency', value)}
                  placeholder={t('sales.quotes.currency')}
                  searchPlaceholder="Search..."
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="discountPercent">{t('sales.quotes.globalDiscount')} %</Label>
                <Input
                  type="number"
                  id="discountPercent"
                  {...register('discountPercent', { valueAsNumber: true })}
                  min="0"
                  max="100"
                  step="0.01"
                />
              </div>
            </div>
          </div>

          {/* Quote Items */}
          <div className="border border-border bg-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="flex items-center gap-2 font-semibold">
                <Package className="h-5 w-5" />
                {t('sales.quotes.items')} ({items.length})
              </h2>
              <Button type="button" variant="outline" size="sm" onClick={handleAddCustomLine}>
                <Plus className="mr-1 h-4 w-4" />
                {t('sales.orders.addCustomLine')}
              </Button>
            </div>

            {/* Product Search */}
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder={t('products.searchPlaceholder')}
                  value={productSearch}
                  onChange={(e) => setProductSearch(e.target.value)}
                  className="pl-10"
                />
              </div>

              {productSearch.length === 0 && (
                <p className="mt-2 text-xs text-muted-foreground">
                  {t('sales.quotes.searchHint')}
                </p>
              )}

              {productSearch.length > 0 && productSearch.length < 2 && (
                <p className="mt-2 text-xs text-muted-foreground">
                  {t('sales.quotes.typeMoreChars')}
                </p>
              )}

              {productSearch.length >= 2 && products.length > 0 && (
                <div className="mt-2 border border-border divide-y max-h-48 overflow-y-auto">
                  {products.map((product) => (
                    <button
                      key={product.id}
                      type="button"
                      className="w-full p-3 text-left hover:bg-muted/50 transition-colors flex items-center justify-between"
                      onClick={() => handleAddProduct(product.id)}
                    >
                      <div>
                        <p className="font-medium text-sm">{product.name}</p>
                        <p className="text-xs text-muted-foreground">{product.fullCode}</p>
                      </div>
                      <Plus className="h-4 w-4 text-muted-foreground" />
                    </button>
                  ))}
                </div>
              )}

              {productSearch.length >= 2 && products.length === 0 && (
                <p className="mt-2 text-sm text-muted-foreground">
                  {t('products.noProducts')}
                </p>
              )}
            </div>

            {/* Items List */}
            {items.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center border border-border bg-muted/30">
                <ShoppingCart className="h-10 w-10 text-muted-foreground/50 mb-2" />
                <p className="text-sm text-muted-foreground">
                  {t('sales.quotes.noItems')}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {t('sales.quotes.searchAndAddProducts')}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {items.map((field, index) => {
                  const item = watchedValues.items[index];
                  const discountedPrice = (item?.unitPrice || 0) * (1 - (item?.discountPercent || 0) / 100);
                  const lineSubtotal = (item?.quantity || 0) * discountedPrice;
                  const lineTax = lineSubtotal * ((item?.taxRate || 0) / 100);
                  const lineTotal = lineSubtotal + lineTax;

                  return (
                    <div key={field.id} className="border border-border p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          {field.productId ? (
                            <>
                              <p className="font-medium">{field.productName}</p>
                              <p className="text-xs text-muted-foreground font-mono">{field.productCode}</p>
                            </>
                          ) : (
                            <div className="space-y-1">
                              <Label className="text-xs">{t('sales.orders.customLineDescription')}</Label>
                              <Input
                                {...register(`items.${index}.description`)}
                                placeholder={t('sales.orders.customLineDescriptionPlaceholder')}
                              />
                            </div>
                          )}
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeItem(index)}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>

                      <div className="grid gap-3 sm:grid-cols-5">
                        <div className="space-y-1">
                          <Label className="text-xs">{t('sales.quotes.quantity')}</Label>
                          <Input
                            type="number"
                            {...register(`items.${index}.quantity`, { valueAsNumber: true })}
                            min="1"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">{t('sales.quotes.unitPrice')}</Label>
                          <Input
                            type="number"
                            {...register(`items.${index}.unitPrice`, { valueAsNumber: true })}
                            step="0.01"
                            min="0"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">{t('sales.quotes.itemDiscount')} %</Label>
                          <Input
                            type="number"
                            {...register(`items.${index}.discountPercent`, { valueAsNumber: true })}
                            step="0.01"
                            min="0"
                            max="100"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">{t('sales.quotes.tax')} %</Label>
                          <Input
                            type="number"
                            {...register(`items.${index}.taxRate`, { valueAsNumber: true })}
                            step="0.01"
                            min="0"
                            max="100"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">{t('sales.quotes.notes')}</Label>
                          <Input
                            {...register(`items.${index}.notes`)}
                            placeholder="Optional"
                          />
                        </div>
                      </div>

                      <div className="flex justify-end mt-3 pt-3 border-t">
                        <span className="text-sm">
                          <span className="text-muted-foreground">{t('sales.quotes.lineTotal_label')}</span>
                          <span className="font-medium">
                            {formatCurrency(lineTotal, watchedValues.currency)}
                          </span>
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Terms & Notes */}
          <div className="border border-border bg-card p-6">
            <h2 className="flex items-center gap-2 font-semibold mb-4">
              <FileText className="h-5 w-5" />
              {t('sales.quotes.termsAndNotes')}
            </h2>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="paymentTerms">{t('sales.quotes.paymentTerms')}</Label>
                <Input
                  id="paymentTerms"
                  {...register('paymentTerms')}
                  placeholder={t('sales.quotes.paymentTermsPlaceholder')}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="deliveryTerms">{t('sales.quotes.deliveryTerms')}</Label>
                <Input
                  id="deliveryTerms"
                  {...register('deliveryTerms')}
                  placeholder={t('sales.quotes.deliveryTermsPlaceholder')}
                />
              </div>
            </div>

            <div className="mt-4 space-y-2">
              <Label htmlFor="notes">{t('sales.quotes.notes')}</Label>
              <Textarea
                id="notes"
                {...register('notes')}
                placeholder={t('sales.quotes.notesPlaceholder')}
                rows={3}
              />
            </div>
          </div>
        </div>

        {/* Sidebar - Quote Summary */}
        <div className="space-y-6">
          <div className="border border-border bg-card p-6 sticky top-6">
            <h2 className="flex items-center gap-2 font-semibold mb-4">
              <Calculator className="h-5 w-5" />
              {t('sales.quotes.summary')}
            </h2>

            <div className="space-y-3 text-sm">
              {selectedCustomer && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('sales.quotes.customer')}</span>
                  <span className="font-medium">{selectedCustomer.companyName || selectedCustomer.contactName}</span>
                </div>
              )}

              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('sales.quotes.validUntil')}</span>
                <span className="font-medium">
                  {format(addDays(new Date(), watchedValues.validDays || 30), 'MMM d, yyyy')}
                </span>
              </div>

              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('sales.quotes.items')}</span>
                <span className="font-medium">{items.length}</span>
              </div>

              <div className="border-t my-3" />

              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('sales.quotes.subtotal')}</span>
                <span>{formatCurrency(subtotal, watchedValues.currency)}</span>
              </div>

              {discountAmount > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>{t('sales.quotes.discount')} ({watchedValues.discountPercent}%)</span>
                  <span>-{formatCurrency(discountAmount, watchedValues.currency)}</span>
                </div>
              )}

              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('sales.quotes.tax')}</span>
                <span>{formatCurrency(taxAmount, watchedValues.currency)}</span>
              </div>

              <div className="flex justify-between text-base pt-2 border-t">
                <span className="font-semibold">{t('sales.quotes.total')}</span>
                <span className="font-bold">
                  {formatCurrency(total, watchedValues.currency)}
                </span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="mt-6 space-y-2">
              <Button
                type="button"
                className="w-full"
                onClick={handleSubmit(onSubmit)}
                disabled={createMutation.isPending || items.length === 0}
              >
                {createMutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {t('sales.quotes.createDraft')}
              </Button>
              <p className="text-xs text-center text-muted-foreground">
                {t('sales.quotes.draftNote')}
              </p>
            </div>

            {/* Validation Warnings */}
            {(!watchedValues.contactName || !watchedValues.email || items.length === 0) && (
              <div className="mt-4 space-y-2">
                {!watchedValues.contactName && (
                  <p className="text-xs text-yellow-600">{t('sales.validation.enterContactName')}</p>
                )}
                {!watchedValues.email && (
                  <p className="text-xs text-yellow-600">{t('sales.validation.enterEmail')}</p>
                )}
                {items.length === 0 && (
                  <p className="text-xs text-yellow-600">{t('sales.validation.addAtLeastOneProduct')}</p>
                )}
              </div>
            )}
          </div>
        </div>
      </form>
    </div>
  );
}
